export type DesignStudioSlideshowPlacement = 'top' | 'bottom'

export type DesignStudioSlide = {
  id: string
  storagePath: string
  caption?: string
  sortOrder: number
}

export type DesignStudioSlideshowSettings = {
  top: DesignStudioSlide[]
  bottom: DesignStudioSlide[]
  introVideo?: DesignStudioIntroVideo | null
}

export type DesignStudioIntroVideo = {
  url: string
  posterUrl?: string
}

export const DEFAULT_DESIGN_STUDIO_VIDEO: DesignStudioIntroVideo = {
  url: '/media/design-studio/bliss-room-to-checkout-9s-v2.mp4',
  posterUrl: '/media/design-studio/bliss-room-to-checkout-v2.jpg',
}

export function publicMediaUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined
  try {
    const url = new URL(value.trim())
    if (url.protocol !== 'https:' || url.username || url.password) return undefined
    return url.href
  } catch {
    return undefined
  }
}

export function normalizeIntroVideo(value: unknown): DesignStudioIntroVideo | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const video = value as Record<string, unknown>
  const url = publicMediaUrl(video.url)
  return url ? { url, posterUrl: publicMediaUrl(video.posterUrl) } : undefined
}

export const DESIGN_STUDIO_SLIDESHOW_SETTINGS_KEY = 'blissDesignStudioSlideshows'

export const EMPTY_DESIGN_STUDIO_SLIDESHOW: DesignStudioSlideshowSettings = {
  top: [],
  bottom: [],
}

export function normalizeDesignStudioSlideshowSettings(
  raw: unknown
): DesignStudioSlideshowSettings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { top: [], bottom: [] }
  }
  const o = raw as Record<string, unknown>
  const parseList = (value: unknown): DesignStudioSlide[] => {
    if (!Array.isArray(value)) return []
    const parsed = value
      .map((item, index): DesignStudioSlide | null => {
        if (!item || typeof item !== 'object') return null
        const row = item as Record<string, unknown>
        const id = typeof row.id === 'string' ? row.id : ''
        const storagePath = typeof row.storagePath === 'string' ? row.storagePath : ''
        if (!id || !storagePath) return null
        return {
          id,
          storagePath,
          caption: typeof row.caption === 'string' ? row.caption : undefined,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : index,
        }
      })
      .filter((item): item is DesignStudioSlide => item !== null)
    parsed.sort((a, b) => a.sortOrder - b.sortOrder)
    return parsed
  }
  return {
    top: parseList(o.top),
    bottom: parseList(o.bottom),
    introVideo: o.introVideo === null ? null : normalizeIntroVideo(o.introVideo),
  }
}
